import { Component, DestroyRef, Input, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { InspectionScope, isEmptyScope } from '../../../core/interfaces/inspection-scope';
import { LocalizedLabelService } from '../../../core/services/utils/localized-label.service';

interface LevelLine { id: number; title: string; subjects: string[] }

/**
 * Le périmètre d'inspection en une phrase — « tout l'établissement », ou les
 * matières par niveau. Le même résumé partout où le périmètre se lit : dans le
 * dialogue d'attribution et en tête de la liste des séances à inspecter.
 */
@Component({
  selector: 'app-inspection-scope-summary',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './inspection-scope-summary.component.html',
  styleUrl: './inspection-scope-summary.component.css',
})
export class InspectionScopeSummaryComponent {
  private readonly labels = inject(LocalizedLabelService);
  private readonly translate = inject(TranslateService);
  private readonly value = signal<InspectionScope | null>(null);

  /** La langue, en signal : les libellés suivent quand l'utilisateur en change. */
  private readonly lang = signal(this.translate.currentLang);

  constructor() {
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe((e) => this.lang.set(e.lang));
  }

  @Input() set scope(scope: InspectionScope | null | undefined) {
    this.value.set(scope ?? null);
  }

  readonly isAll = computed(() => this.value()?.all === true);
  readonly isEmpty = computed(() => isEmptyScope(this.value()));

  readonly levels = computed<LevelLine[]>(() => {
    this.lang(); // dépendance explicite : recalculer au changement de langue
    const byLevel = new Map<number, LevelLine>();
    for (const item of this.value()?.items ?? []) {
      const line = byLevel.get(item.learningPathId)
        ?? { id: item.learningPathId, title: this.labels.pickPrefixed(item as unknown as Record<string, unknown>, 'learningPathTitle') || String(item.learningPathId), subjects: [] };
      line.subjects.push(this.labels.pickPrefixed(item as unknown as Record<string, unknown>, 'subjectName') || String(item.subjectId));
      byLevel.set(item.learningPathId, line);
    }
    return [...byLevel.values()];
  });

  readonly subjectCount = computed(() => new Set((this.value()?.items ?? []).map((i) => i.subjectId)).size);
}
